/**
 * Phone number validation utility functions
 */

/**
 * Phone number validation result interface
 */
export interface PhoneValidationResult {
  isValid: boolean;
  formattedNumber?: string;
  countryCode?: string;
  nationalNumber?: string;
  errorMessage?: string;
}

/**
 * Common phone number patterns for different countries
 */
export const PHONE_PATTERNS = {
  // International formats
  INTERNATIONAL: /^\+[1-9]\d{1,14}$/,

  // US/Canada
  US_CANADA: /^(\+1[-.\s]?)?\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/,

  // UK
  UK: /^(\+44[-.\s]?)?(\(0\))?[-.\s]?([1-9]\d{2,3})[-.\s]?(\d{3,4})[-.\s]?(\d{3,4})$/,

  // Vietnam
  VIETNAM: /^(?:\+84|0)(?:[35789]\d{8}|2\d{9})$/,

  // Generic mobile (7-15 digits)
  MOBILE: /^(\+?[1-9]\d{0,3}[-.\s]?)?\d{7,15}$/,

  // Generic landline (7-15 digits)
  LANDLINE: /^(\+?[1-9]\d{0,3}[-.\s]?)?\d{7,15}$/,
} as const;

/**
 * Country codes for phone number validation
 */
export const COUNTRY_CODES = {
  US: '+1',
  CA: '+1',
  UK: '+44',
  VN: '+84',
  AU: '+61',
  DE: '+49',
  FR: '+33',
  JP: '+81',
  KR: '+82',
  CN: '+86',
  IN: '+91',
} as const;

/**
 * Validates if a phone number is in a valid format
 * @param phoneNumber - The phone number to validate
 * @param country - Optional country code for specific validation
 * @returns PhoneValidationResult object with validation details
 * @example
 * validatePhoneNumber("+1234567890") // returns { isValid: true, ... }
 * validatePhoneNumber("123-456-7890", "US") // returns { isValid: true, ... }
 * validatePhoneNumber("invalid") // returns { isValid: false, errorMessage: "..." }
 */
export function validatePhoneNumber(
  phoneNumber: string,
  country?: keyof typeof COUNTRY_CODES
): PhoneValidationResult {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return {
      isValid: false,
      errorMessage: 'Phone number is required and must be a string',
    };
  }

  // Clean the phone number (remove spaces, dashes, parentheses)
  const cleanedNumber = phoneNumber.replace(/[\s\-().]/g, '');

  if (cleanedNumber.length === 0) {
    return {
      isValid: false,
      errorMessage: 'Phone number cannot be empty',
    };
  }

  // Check for minimum length
  if (cleanedNumber.length < 7) {
    return {
      isValid: false,
      errorMessage: 'Phone number is too short (minimum 7 digits)',
    };
  }

  // Check for maximum length
  if (cleanedNumber.length > 15) {
    return {
      isValid: false,
      errorMessage: 'Phone number is too long (maximum 15 digits)',
    };
  }

  // Country-specific validation
  if (country) {
    return validatePhoneNumberByCountry(cleanedNumber, country);
  }

  // Generic validation
  return validateGenericPhoneNumber(cleanedNumber);
}

/**
 * Validates phone number for specific country
 * @param phoneNumber - The cleaned phone number
 * @param country - Country code
 * @returns PhoneValidationResult object
 */
function validatePhoneNumberByCountry(
  phoneNumber: string,
  country: keyof typeof COUNTRY_CODES
): PhoneValidationResult {
  switch (country) {
    case 'US':
    case 'CA':
      return validateUSCanadaPhone(phoneNumber);
    case 'UK':
      return validateUKPhone(phoneNumber);
    case 'VN':
      return validateVietnamPhone(phoneNumber);
    default:
      return validateGenericPhoneNumber(phoneNumber);
  }
}

/**
 * Validates US/Canada phone numbers
 */
function validateUSCanadaPhone(phoneNumber: string): PhoneValidationResult {
  const usPattern = /^(\+1)?(\d{10})$/;
  const match = usPattern.exec(phoneNumber);

  if (!match) {
    return {
      isValid: false,
      errorMessage: 'Invalid US/Canada phone number format',
    };
  }

  const fullNumber = match[2];
  const areaCode = fullNumber.substring(0, 3);
  const exchange = fullNumber.substring(3, 6);
  const number = fullNumber.substring(6);

  return {
    isValid: true,
    formattedNumber: `+1 (${areaCode}) ${exchange}-${number}`,
    countryCode: '+1',
    nationalNumber: `(${areaCode}) ${exchange}-${number}`,
  };
}

/**
 * Validates UK phone numbers
 */
function validateUKPhone(phoneNumber: string): PhoneValidationResult {
  const ukPattern = /^(\+44)?([1-9]\d{8,9})$/;
  const match = ukPattern.exec(phoneNumber);

  if (!match) {
    return {
      isValid: false,
      errorMessage: 'Invalid UK phone number format',
    };
  }

  const nationalNumber = match[2];

  return {
    isValid: true,
    formattedNumber: `+44 ${nationalNumber}`,
    countryCode: '+44',
    nationalNumber: nationalNumber,
  };
}

/**
 * Validates Vietnam phone numbers
 * Accepts all valid Vietnamese phone number formats
 */
function validateVietnamPhone(phoneNumber: string): PhoneValidationResult {
  // Comprehensive pattern for Vietnamese phone numbers
  const vnPattern = /^(?:\+84|0)(?:[35789]\d{8}|2\d{9})$/;
  const match = vnPattern.exec(phoneNumber);

  if (!match) {
    return {
      isValid: false,
      errorMessage: 'common.validation.invalid_phone_format',
    };
  }

  // Extract the national number (remove +84 or 0 prefix)
  const nationalNumber = phoneNumber.startsWith('+84')
    ? phoneNumber.substring(3)
    : phoneNumber.substring(1);

  return {
    isValid: true,
    formattedNumber: `+84 ${nationalNumber}`,
    countryCode: '+84',
    nationalNumber: nationalNumber,
  };
}

/**
 * Validates generic phone numbers
 */
function validateGenericPhoneNumber(
  phoneNumber: string
): PhoneValidationResult {
  // Check if it starts with + (international format)
  if (phoneNumber.startsWith('+')) {
    if (!PHONE_PATTERNS.INTERNATIONAL.test(phoneNumber)) {
      return {
        isValid: false,
        errorMessage: 'common.validation.invalid_international_phone_format',
      };
    }

    return {
      isValid: true,
      formattedNumber: phoneNumber,
      countryCode: phoneNumber.substring(0, phoneNumber.length - 10), // Assume last 10 digits are national
      nationalNumber: phoneNumber.substring(phoneNumber.length - 10),
    };
  }

  // Check if it's a valid mobile/landline format
  if (!PHONE_PATTERNS.MOBILE.test(phoneNumber)) {
    return {
      isValid: false,
      errorMessage: 'common.validation.invalid_phone_number_format',
    };
  }

  return {
    isValid: true,
    formattedNumber: phoneNumber,
    nationalNumber: phoneNumber,
  };
}

/**
 * Formats a phone number for display
 * @param phoneNumber - The phone number to format
 * @param country - Optional country for specific formatting
 * @returns Formatted phone number string
 * @example
 * formatPhoneNumber("1234567890", "US") // returns "+1 (123) 456-7890"
 * formatPhoneNumber("+84123456789", "VN") // returns "+84 123 456 789"
 */
export function formatPhoneNumber(
  phoneNumber: string,
  country?: keyof typeof COUNTRY_CODES
): string {
  const validation = validatePhoneNumber(phoneNumber, country);

  if (!validation.isValid) {
    return phoneNumber; // Return original if invalid
  }

  return validation.formattedNumber || phoneNumber;
}

/**
 * Extracts country code from international phone number
 * @param phoneNumber - The international phone number
 * @returns Country code or null if not found
 * @example
 * extractCountryCode("+1234567890") // returns "+1"
 * extractCountryCode("+84123456789") // returns "+84"
 */
export function extractCountryCode(phoneNumber: string): string | null {
  if (!phoneNumber.startsWith('+')) {
    return null;
  }

  // Common country code lengths
  const possibleLengths = [1, 2, 3, 4];

  for (const length of possibleLengths) {
    const code = phoneNumber.substring(0, length + 1); // +1, +44, +84, etc.
    if (Object.values(COUNTRY_CODES).includes(code as any)) {
      return code;
    }
  }

  return null;
}

/**
 * Checks if a phone number is international format
 * @param phoneNumber - The phone number to check
 * @returns True if international format, false otherwise
 * @example
 * isInternationalFormat("+1234567890") // returns true
 * isInternationalFormat("1234567890") // returns false
 */
export function isInternationalFormat(phoneNumber: string): boolean {
  return phoneNumber.startsWith('+');
}

/**
 * Normalizes a phone number by removing formatting
 * @param phoneNumber - The phone number to normalize
 * @returns Normalized phone number (digits only with optional +)
 * @example
 * normalizePhoneNumber("+1 (123) 456-7890") // returns "+11234567890"
 * normalizePhoneNumber("123-456-7890") // returns "1234567890"
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) return '';

  // Remove all formatting characters except +
  return phoneNumber.replace(/[\s\-().]/g, '');
}

/**
 * Normalize Vietnamese phone numbers when a CSV/Excel import drops the leading "0".
 *
 * Examples:
 * - "708788284" -> "0708788284"
 * - "+84999999999" -> "0999999999"
 */
export function normalizeVietnamPhoneNumber(phoneNumber: string): string {
  const normalized = normalizePhoneNumber(phoneNumber).trim();
  if (!normalized) return '';

  // Convert +84 to leading 0
  if (normalized.startsWith('+84')) {
    return `0${normalized.substring(3)}`;
  }

  // Sometimes inputs come without "+" (e.g. "8499....")
  if (normalized.startsWith('84')) {
    return `0${normalized.substring(2)}`;
  }

  // Already has leading 0
  if (normalized.startsWith('0')) {
    return normalized;
  }

  // Excel/CSV can drop leading "0" => VN mobile: 9 digits (starts with 3/5/7/8/9)
  if (/^[35789]\d{8}$/.test(normalized)) {
    return `0${normalized}`;
  }

  // VN landline without leading 0: 10 digits starting with 2
  if (/^2\d{9}$/.test(normalized)) {
    return `0${normalized}`;
  }

  return normalized;
}

/**
 * Validates phone number for Angular reactive forms
 * @param control - Angular form control
 * @returns Validation error object or null
 * @example
 * // In component:
 * phoneValidator = phoneNumberValidator;
 *
 * // In form:
 * phone: ['', [Validators.required, this.phoneValidator]]
 */
export function phoneNumberValidator(control: any) {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }

  const validation = validatePhoneNumber(control.value);

  if (!validation.isValid) {
    return {
      invalidPhone: {
        message:
          validation.errorMessage ||
          'common.validation.invalid_phone_number_format',
      },
    };
  }

  return null;
}

/**
 * Validates Vietnamese phone number for Angular reactive forms
 * @param control - Angular form control
 * @returns Validation error object or null
 * @example
 * // In component:
 * vietnamesePhoneValidator = vietnamesePhoneNumberValidator;
 *
 * // In form:
 * recipientNumber: ['', [Validators.required, this.vietnamesePhoneValidator]]
 */
export function vietnamesePhoneNumberValidator(control: any) {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }

  const normalized = normalizeVietnamPhoneNumber(String(control.value));
  const validation = validatePhoneNumber(normalized, 'VN');

  if (!validation.isValid) {
    return {
      invalidVietnamesePhone: {
        message:
          validation.errorMessage || 'common.validation.invalid_phone_format',
      },
    };
  }

  return null;
}
